import { useCallback } from 'react';

interface UseSectionFilterHandlersParams {
  sectionHasFeatures: (sectionKey: string) => boolean;
  handleFeatureChange: (featureKey: string, isActive: boolean, sectionKey: string) => void;
  cancelSectionRequest: (sectionKey: string) => void;
  setLoadingFeatures: React.Dispatch<React.SetStateAction<Record<string, Record<string, boolean>>>>;
}

export const useSectionFilterHandlers = ({
  sectionHasFeatures,
  handleFeatureChange,
  cancelSectionRequest,
  setLoadingFeatures,
}: UseSectionFilterHandlersParams) => {
  const handleFeatureChangeWithRefetch = useCallback(
    async (featureKey: string, isActive: boolean, sectionKey: string) => {
      if (!sectionHasFeatures(sectionKey)) return;

      try {
        // Cancel any existing request for this section
        cancelSectionRequest(sectionKey);

        // Set loading state immediately
        setLoadingFeatures((prev) => ({
          ...prev,
          [sectionKey]: {
            ...prev[sectionKey],
            [featureKey]: true,
          },
        }));

        // Update feature state
        handleFeatureChange(featureKey, isActive, sectionKey);

        // The useFetchData hook will automatically trigger a refetch due to the feature change
        // No need to manually call refetchSectionData here
      } catch (error) {
      } finally {
        // Clear loading state after a brief delay to show the loading indicator
        setTimeout(() => {
          setLoadingFeatures((prev) => ({
            ...prev,
            [sectionKey]: {
              ...prev[sectionKey],
              [featureKey]: false,
            },
          }));
        }, 500); // Keep loading indicator visible for user feedback
      }
    },
    [handleFeatureChange, sectionHasFeatures, cancelSectionRequest, setLoadingFeatures]
  );

  return { handleFeatureChangeWithRefetch };
};
